import { useState, useCallback, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, reset } from "../features/auth/authSlice";
import { getMyOrgs, setWorkspace } from "../features/org/orgSlice";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Tooltip,
  MenuItem,
  Button,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import FolderIcon from "@mui/icons-material/Folder";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LinkIcon from "@mui/icons-material/Link";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import { toast } from "react-toastify";

import BowtieIcon from "./icons/BowtieIcon";
import MyFilesModal from "./MyFilesModal";
import BowtieEditSidePanel from "../components/sidepanels/BowtieEditSidePanel";
import HelpModal from "./HelpModal";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import CreateOrgDialog from "./CreateOrgDialog";

function Header() {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [openBowtieEditSidePanel, setOpenBowtieEditSidePanel] = useState(false);
  const [openMyFilesModal, setOpenMyFilesModal] = useState(false);
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { myOrgs, activeWorkspace } = useSelector((state) => state.org);

  // Check if user has active subscription
  const hasActiveSubscription = user?.hasActiveSubscription || false;

  // Load the user's organisations once they're authenticated.
  useEffect(() => {
    if (user?.token) {
      dispatch(getMyOrgs());
    }
  }, [user?.token, dispatch]);

  // Viewer page detection
  const viewerMatch = location.pathname.match(/^\/viewer\/([^/?]+)/);
  const viewerBowtieId = viewerMatch ? viewerMatch[1] : null;
  const isViewerPage = Boolean(viewerBowtieId);

  // Check current page
  const isHomePage = location.pathname === "/";
  const isLoginPage = location.pathname === "/login";
  const isRegisterPage = location.pathname === "/register";

  // Viewer handlers
  const handleShareMenuOpen = useCallback((e) => setShareMenuAnchor(e.currentTarget), []);
  const handleShareMenuClose = useCallback(() => setShareMenuAnchor(null), []);

  const handleCopyViewerLink = useCallback(() => {
    handleShareMenuClose();
    const url = `${window.location.origin}/viewer/${viewerBowtieId}?utm_source=viewer_share`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success("Link copied to clipboard");
    }).catch(() => {
      toast.error("Could not copy link");
    });
  }, [viewerBowtieId, handleShareMenuClose]);

  const handleDownloadPdf = useCallback(() => {
    handleShareMenuClose();
    window.dispatchEvent(new CustomEvent('bowtie-pdf-download'));
  }, [handleShareMenuClose]);

  const handleEditBowtie = useCallback(async () => {
    if (!user) {
      navigate(`/register?template=${viewerBowtieId}`);
      return;
    }
    setCloning(true);
    try {
      const { data } = await axios.post(`/api/viewer/${viewerBowtieId}/clone`, {}, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      navigate(`/editor/${data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not copy this bowtie");
      setCloning(false);
    }
  }, [viewerBowtieId, user, navigate]);

  // Event handlers
  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const handleOpenBowtieEditSidePanel = () => {
    setOpenBowtieEditSidePanel(true);
    handleCloseNavMenu();
    handleCloseUserMenu();
  };

  const handleOpenMyFilesModal = () => {
    setOpenMyFilesModal(true);
    handleCloseNavMenu();
    handleCloseUserMenu();
  };

  const handleCreateBowtieFromFiles = () => {
    setOpenMyFilesModal(false);
    setOpenBowtieEditSidePanel(true);
  };

  const handleOpenHelpModal = () => {
    setOpenHelpModal(true);
    handleCloseNavMenu();
    handleCloseUserMenu();
  };

  const handleNavigation = (path) => {
    handleCloseNavMenu();
    handleCloseUserMenu();
    navigate(path);
  };

  const handleExternalNavigation = (url) => {
    handleCloseNavMenu();
    handleCloseUserMenu();
    window.location.href = url;
  };

  const handleLogout = () => {
    dispatch(logout());
    dispatch(reset());
    handleCloseUserMenu();
    navigate("/");
  };

  const handleHomeClick = () => {
    if (!isHomePage) {
      navigate("/");
    }
  };

  const handlePickWorkspace = (workspace) => {
    handleCloseNavMenu();
    if (workspace === activeWorkspace) {
      return;
    }
    dispatch(setWorkspace(workspace));

    // Leave any page that's tied to the workspace you're switching away from.
    const path = location.pathname;
    if (path.startsWith("/editor")) {
      navigate("/dashboard");
    } else if (path.startsWith("/orgs/")) {
      navigate(workspace === "personal" ? "/dashboard" : `/orgs/${workspace}`);
    }
  };

  const handleOpenCreateOrg = () => {
    handleCloseNavMenu();
    setCreateOrgOpen(true);
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "#2C3E50",
          color: "#FFFFFF",
          boxShadow: "0 2px 8px rgba(44, 62, 80, 0.15)",
          border: "none",
          "& .MuiToolbar-root": {
            minHeight: "64px",
          },
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ position: "relative" }}>
            {/* Mobile Layout - Left: Hamburger Menu */}
            {user && (
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  position: "absolute",
                  left: 0,
                  zIndex: 10,
                }}
              >
                <IconButton
                  size="large"
                  aria-label="navigation menu"
                  onClick={handleOpenNavMenu}
                  sx={{
                    color: "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorElNav}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{
                    display: { xs: "block", md: "none" },
                    "& .MuiPaper-root": {
                      borderRadius: 2,
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  {hasActiveSubscription && (
                    <MenuItem onClick={handleOpenMyFilesModal}>
                      <FolderIcon sx={{ mr: 2, color: "text.secondary" }} />
                      <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                        My Bowties
                      </Typography>
                    </MenuItem>
                  )}
                  {hasActiveSubscription && (
                    <MenuItem onClick={handleOpenBowtieEditSidePanel}>
                      <AddIcon sx={{ mr: 2, color: "text.secondary" }} />
                      <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                        New Bowtie
                      </Typography>
                    </MenuItem>
                  )}

                  <Divider />
                  <Typography
                    variant="caption"
                    sx={{ px: 2, py: 0.5, color: "text.secondary", display: "block" }}
                  >
                    Workspace
                  </Typography>

                  <MenuItem
                    selected={activeWorkspace === "personal"}
                    onClick={() => handlePickWorkspace("personal")}
                  >
                    <ListItemIcon>
                      {activeWorkspace === "personal" ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <PersonIcon fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText>Personal</ListItemText>
                  </MenuItem>

                  {myOrgs.map((org) => (
                    <MenuItem
                      key={org._id}
                      selected={activeWorkspace === org.slug}
                      onClick={() => handlePickWorkspace(org.slug)}
                    >
                      <ListItemIcon>
                        {activeWorkspace === org.slug ? (
                          <CheckIcon fontSize="small" />
                        ) : (
                          <GroupsIcon fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText primary={org.name} secondary={org.role} />
                    </MenuItem>
                  ))}

                  <MenuItem onClick={handleOpenCreateOrg}>
                    <ListItemIcon>
                      <AddIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </ListItemIcon>
                    <ListItemText>Create organisation…</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            )}

            {/* Mobile Layout - Center: Logo & Brand */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                cursor: isHomePage ? "default" : "pointer",
                "&:hover": isHomePage
                  ? {}
                  : {
                      opacity: 0.8,
                      transition: "opacity 0.2s ease",
                    },
              }}
              onClick={handleHomeClick}
            >
              <BowtieIcon sx={{ mr: 1 }} />
              <Typography
                variant="h6"
                noWrap
                sx={{
                  userSelect: "none",
                  fontFamily: "Roboto Mono, monospace",
                  fontWeight: 700,
                  color: "#FFFFFF",
                  textDecoration: "none",
                  opacity: isHomePage ? 0.7 : 1,
                }}
              >
                Bowtie
              </Typography>
            </Box>

            {/* Mobile Layout - Right: User Account (for logged-in users) */}
            {user && (
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  position: "absolute",
                  right: 0,
                  zIndex: 10,
                }}
              >
                <Tooltip title="My Account">
                  <IconButton
                    onClick={handleOpenUserMenu}
                    sx={{
                      p: 0.5,
                      "&:hover": {
                        transform: "scale(1.1)",
                        transition: "transform 0.2s ease",
                      },
                    }}
                  >
                    <Avatar
                      alt={user?.firstName || "User"}
                      sx={{
                        bgcolor: "#E67E22",
                        border: "2px solid rgba(255, 255, 255, 0.2)",
                        width: 36,
                        height: 36,
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      {user?.firstName?.charAt(0) ||
                        user?.email?.charAt(0) ||
                        "U"}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Mobile Layout - Guest Users: Hamburger Menu */}
            {!user && (
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  position: "absolute",
                  right: 0,
                  zIndex: 10,
                }}
              >
                <IconButton
                  size="large"
                  aria-label="navigation menu"
                  onClick={handleOpenNavMenu}
                  sx={{
                    color: "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorElNav}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{
                    display: { xs: "block", md: "none" },
                    "& .MuiPaper-root": {
                      borderRadius: 2,
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <MenuItem
                    onClick={() =>
                      handleExternalNavigation("https://training.bowtie.app")
                    }
                  >
                    <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                      Training
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleNavigation("/connect-your-ai-assistant")}>
                    <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                      AI Assistant
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleNavigation("/api-docs")}>
                    <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                      API Docs
                    </Typography>
                  </MenuItem>
                  {!isLoginPage && (
                    <MenuItem onClick={() => handleNavigation("/login")}>
                      <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                        Login
                      </Typography>
                    </MenuItem>
                  )}
                  {!isRegisterPage && (
                    <MenuItem onClick={() => handleNavigation("/register")}>
                      <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                        Register
                      </Typography>
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            )}

            {/* Mobile Layout - Guest Users: Centered Logo & Brand */}
            {!user && (
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  alignItems: "center",
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  cursor: isHomePage ? "default" : "pointer",
                  "&:hover": isHomePage
                    ? {}
                    : {
                        opacity: 0.8,
                        transition: "opacity 0.2s ease",
                      },
                }}
                onClick={handleHomeClick}
              >
                <BowtieIcon sx={{ mr: 1 }} />
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    userSelect: "none",
                    fontFamily: "Roboto Mono, monospace",
                    fontWeight: 700,
                    color: "#FFFFFF",
                    textDecoration: "none",
                    opacity: isHomePage ? 0.7 : 1,
                  }}
                >
                  Bowtie
                </Typography>
              </Box>
            )}

            {/* Desktop Layout - Left: Logo & Brand */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                cursor: isHomePage ? "default" : "pointer",
                "&:hover": isHomePage
                  ? {}
                  : {
                      opacity: 0.8,
                      transition: "opacity 0.2s ease",
                    },
              }}
              onClick={handleHomeClick}
            >
              <BowtieIcon
                sx={{
                  mr: 1,
                  "&:hover": isHomePage
                    ? {}
                    : {
                        transform: "scale(1.1)",
                        transition: "transform 0.2s ease",
                      },
                }}
              />
              <Typography
                variant="h6"
                noWrap
                sx={{
                  userSelect: "none",
                  mr: 2,
                  fontFamily: "Roboto Mono, monospace",
                  fontWeight: 700,
                  color: "#FFFFFF",
                  textDecoration: "none",
                  opacity: isHomePage ? 0.7 : 1,
                }}
              >
                Bowtie
              </Typography>
            </Box>

            {/* Desktop Navigation (for logged-in users) */}
            {user && (
              <Box
                sx={{
                  flexGrow: 1,
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {hasActiveSubscription && (
                  <Button
                    onClick={handleOpenMyFilesModal}
                    startIcon={<FolderIcon />}
                    sx={{
                      color: "#FFFFFF",
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        transform: "translateY(-1px)",
                        transition: "all 0.2s ease",
                      },
                    }}
                  >
                    My Bowties
                  </Button>
                )}
                {hasActiveSubscription && (
                  <Button
                    onClick={handleOpenBowtieEditSidePanel}
                    startIcon={<AddIcon />}
                    sx={{
                      color: "#FFFFFF",
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        transform: "translateY(-1px)",
                        transition: "all 0.2s ease",
                      },
                    }}
                  >
                    New Bowtie
                  </Button>
                )}
              </Box>
            )}

            {/* Spacer for guest users on desktop */}
            {!user && (
              <Box sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }} />
            )}

            {/* Viewer action buttons — shown on /viewer/:id for all users */}
            {isViewerPage && (
              <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1, mr: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<ArrowDropDownIcon />}
                  onClick={handleShareMenuOpen}
                  sx={{ color: "#FFFFFF", borderColor: "rgba(255,255,255,0.5)", textTransform: "none",
                    "&:hover": { borderColor: "#FFFFFF", backgroundColor: "rgba(255,255,255,0.1)" } }}
                >
                  Share
                </Button>
                <Menu
                  anchorEl={shareMenuAnchor}
                  open={Boolean(shareMenuAnchor)}
                  onClose={handleShareMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem onClick={handleCopyViewerLink}>
                    <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Copy link</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleDownloadPdf}>
                    <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Download PDF</ListItemText>
                  </MenuItem>
                </Menu>
                <Tooltip title={user ? "Copy this bowtie to your account and customise it" : "Create an account to adapt this bowtie"}>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={handleEditBowtie}
                      disabled={cloning}
                      sx={{ backgroundColor: "#E67E22", textTransform: "none", fontWeight: 600,
                        "&:hover": { backgroundColor: "#D35400" } }}
                    >
                      {cloning ? "Opening..." : "Edit this bowtie"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}

            {/* Desktop: Workspace switcher — sits to the left of the account icon */}
            {user && (
              <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", mr: 1 }}>
                <WorkspaceSwitcher />
              </Box>
            )}

            {/* Desktop User Account Menu (for logged-in users) */}
            {user && (
              <Box sx={{ flexGrow: 0, display: { xs: "none", md: "flex" } }}>
                <Tooltip title="My Account">
                  <IconButton
                    onClick={handleOpenUserMenu}
                    sx={{
                      p: 0.5,
                      "&:hover": {
                        transform: "scale(1.1)",
                        transition: "transform 0.2s ease",
                      },
                    }}
                  >
                    <Avatar
                      alt={user?.firstName || "User"}
                      sx={{
                        bgcolor: "#E67E22",
                        border: "2px solid rgba(255, 255, 255, 0.2)",
                        width: 40,
                        height: 40,
                        fontSize: "1rem",
                        fontWeight: 600,
                      }}
                    >
                      {user?.firstName?.charAt(0) ||
                        user?.email?.charAt(0) ||
                        "U"}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorElUser}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                  sx={{
                    mt: "45px",
                    "& .MuiPaper-root": {
                      borderRadius: 2,
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                      minWidth: 180,
                    },
                  }}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem onClick={() => handleNavigation("/account")}>
                    <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                      My Account
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleNavigation("/api-docs")}>
                    <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                      API Docs
                    </Typography>
                  </MenuItem>
                  {hasActiveSubscription ? (
                    <MenuItem onClick={() => handleNavigation("/dashboard")}>
                      <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                        Dashboard
                      </Typography>
                    </MenuItem>
                  ) : (
                    <MenuItem onClick={() => handleNavigation("/pricing")}>
                      <Typography
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          color: "primary.main",
                          fontWeight: 600,
                        }}
                      >
                        Subscribe
                      </Typography>
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleOpenHelpModal}>
                    <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                      Get Help
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
                      Logout
                    </Typography>
                  </MenuItem>
                </Menu>
              </Box>
            )}

            {/* Desktop Navigation (for guests) - Context Aware */}
            {!user && (
              <Box
                sx={{
                  flexGrow: 0,
                  display: { xs: "none", md: "flex" },
                  gap: 1,
                }}
              >
                <Button
                  component="a"
                  href="https://training.bowtie.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#FFFFFF",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    px: 2,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  Training
                </Button>

                <Button
                  component={Link}
                  to="/connect-your-ai-assistant"
                  sx={{
                    color: "#FFFFFF",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    px: 2,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  AI Assistant
                </Button>

                <Button
                  component={Link}
                  to="/api-docs"
                  sx={{
                    color: "#FFFFFF",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    px: 2,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  API Docs
                </Button>

                {!isLoginPage && (
                  <Button
                    component={Link}
                    to="/login"
                    sx={{
                      color: "#FFFFFF",
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      px: 2,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    Login
                  </Button>
                )}

                {!isRegisterPage && (
                  <Button
                    variant="contained"
                    component={Link}
                    to="/register"
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      backgroundColor: "#E67E22",
                      color: "#FFFFFF",
                      px: 3,
                      py: 1,
                      ml: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2,
                      "&:hover": {
                        backgroundColor: "#D35400",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(230, 126, 34, 0.3)",
                      },
                    }}
                  >
                    Register
                  </Button>
                )}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Modals - Now controlled by Header state */}
      <MyFilesModal
        open={openMyFilesModal}
        onClose={() => setOpenMyFilesModal(false)}
        onCreateBowtie={handleCreateBowtieFromFiles}
      />

      <BowtieEditSidePanel
        isOpen={openBowtieEditSidePanel}
        onClose={() => setOpenBowtieEditSidePanel(false)}
        bowtie={null} // Always null for "create new" from header
      />

      <HelpModal
        open={openHelpModal}
        onClose={() => setOpenHelpModal(false)}
      />

      <CreateOrgDialog
        open={createOrgOpen}
        onClose={() => setCreateOrgOpen(false)}
      />
    </>
  );
}

export default Header;
